import React from "react";

import { useToasts } from "react-toast-notifications";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  DELETE_SCHEDULED_EXPORT,
  FETCH_SCHEDULED_EXPORTS,
} from "@gql/data-export";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type DeleteScheduledExport,
  type DeleteScheduledExportVariables,
  type FetchScheduledExports_me_scheduledExports_data,
} from "@north-beam/nb-client-side";
import { PrimaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

interface Props {
  onClose: () => void;
  row?: FetchScheduledExports_me_scheduledExports_data;
}

export function DeleteModal({ onClose, row }: Props) {
  const prettyLogEventPage = usePrettyLogEventPage();

  const [deleteScheduledExport, { loading }] = useNorthbeamMutation<
    DeleteScheduledExport,
    DeleteScheduledExportVariables
  >(DELETE_SCHEDULED_EXPORT, {
    refetchQueries: [
      { query: FETCH_SCHEDULED_EXPORTS, variables: { page: 1 } },
    ],
    awaitRefetchQueries: true,
  });

  const { addToast } = useToasts();

  return (
    <Modal
      isOpen={!!row}
      onClose={onClose}
      titleContent={
        <div className="flex items-center gap-4 text-lg font-semibold">
          <i
            className="fa-regular fa-trash mb-[2px]"
            style={{ fontSize: "1em" }}
          />
          Delete scheduled export
        </div>
      }
    >
      {row ? (
        <>
          <div className="mt-4">
            <span className="font-semibold">Scheduled export:</span>{" "}
            {row.scheduleName}
          </div>
          <div className="mt-4">
            Deleting this scheduled export will mean it will no longer be
            available to you.
          </div>
          <div className="mt-4">This is not a recoverable action.</div>
          <div className="flex items-center justify-end mt-4">
            <PrimaryButton
              className="bg-danger"
              isDisabled={loading}
              loading={loading}
              onClick={async () => {
                prettyLogEventPage({
                  domain: "DataExport",
                  action: "onClick",
                  additionalActionInfo:
                    "Data export management - scheduled export deleted",
                  data: {
                    pageContext: { id: row.id },
                  },
                });

                await deleteScheduledExport({ variables: { id: row.id } });

                addToast(
                  `The scheduled export, ${row.scheduleName}, has been deleted`,
                  {
                    appearance: "success",
                    autoDismiss: true,
                  },
                );
                onClose();
              }}
            >
              Delete
            </PrimaryButton>
          </div>
        </>
      ) : (
        <div className="h-32"></div>
      )}
    </Modal>
  );
}
